import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { useQuery } from '@apollo/client'
import { GET_RECEIVED_ORDERS } from '../graphql/queries'
import { Order } from '../types/order'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { apiTime } from '../lib/format'

// TODO: bump to 90_000 before prod. Kept at 20s during testing so the indicator
// reacts quickly while smoke-testing the external-handling flow.
const RECEIVED_ORDERS_POLL_MS = 20_000

// How often to recompute the oldest-age tier even when no new poll has fired.
// Lets the urgency tier cross from 4m59s → 5m without waiting for the next poll.
const AGE_TICK_MS = 30_000

// `created_at` is ISO (API 2026-10-08); apiTime reads 0 for an unreadable value, so the
// age arithmetic below never sees NaN.
const parseCreatedAt = (raw: string): number => apiTime(raw)

export type ReceivedOrdersUrgency = 'idle' | 'calm' | 'warning' | 'urgent'

interface ReceivedOrdersContextValue {
  orders: Order[]
  count: number
  oldestAgeMinutes: number | null
  urgency: ReceivedOrdersUrgency
  loading: boolean
  isSheetOpen: boolean
  openSheet: () => void
  closeSheet: () => void
  toggleSheet: () => void
  refetch: () => void
}

const ReceivedOrdersContext = createContext<ReceivedOrdersContextValue | null>(null)

const computeUrgency = (oldestAgeMinutes: number | null): ReceivedOrdersUrgency => {
  if (oldestAgeMinutes === null) return 'idle'
  if (oldestAgeMinutes > 10) return 'urgent'
  if (oldestAgeMinutes >= 5) return 'warning'
  return 'calm'
}

export const ReceivedOrdersProvider = ({ children }: { children: ReactNode }) => {
  // This provider wraps EVERY authenticated session, but the `orders` query it
  // polls runs buildOrderListScope server-side, which throws for a role without
  // any order.list.* permission — and the global errorLink toasts that error. For
  // a DeliveryDriver (who holds only delivery.drive) an ungated poll meant an
  // error toast every 20s for the whole shift. Roles that can't see the incoming
  // queue simply don't poll it; the banner/chime consumers get a stable empty list.
  const { hasAnyPermission } = usePermissions()
  const canSeeIncomingOrders = hasAnyPermission([
    PERMISSIONS.ORDER_LIST_TODAY,
    PERMISSIONS.ORDER_LIST_7DAYS,
    PERMISSIONS.ORDER_LIST_ALL,
  ])

  const { data, loading, refetch } = useQuery<{ orders: Order[] }>(GET_RECEIVED_ORDERS, {
    skip: !canSeeIncomingOrders,
    pollInterval: RECEIVED_ORDERS_POLL_MS,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  })

  const orders = data?.orders ?? []
  const count = orders.length

  // Re-tick "now" every AGE_TICK_MS so age-derived urgency updates without a new poll.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), AGE_TICK_MS)
    return () => clearInterval(id)
  }, [])

  const oldestAgeMinutes = useMemo<number | null>(() => {
    if (orders.length === 0) return null
    const oldestCreatedAt = orders.reduce<number>((min, o) => {
      const t = parseCreatedAt(o.created_at)
      // 0 = unreadable (apiTime): skip it rather than age the queue from 1970.
      return t > 0 && t < min ? t : min
    }, Number.POSITIVE_INFINITY)
    if (!Number.isFinite(oldestCreatedAt)) return null
    return Math.max(0, Math.floor((now - oldestCreatedAt) / 60_000))
  }, [orders, now])

  const urgency = computeUrgency(oldestAgeMinutes)

  // Fire-once-on-increment chime hook.
  // First load: prevCountRef === null → skip (don't chime on page open).
  // Subsequent polls: chime if new count > prev count.
  const prevCountRef = useRef<number | null>(null)
  const [chimeTick, setChimeTick] = useState(0)
  useEffect(() => {
    if (loading) return
    const prev = prevCountRef.current
    if (prev !== null && count > prev) {
      setChimeTick(t => t + 1)
    }
    prevCountRef.current = count
  }, [count, loading])
  // Expose chimeTick via context so consumers (chime hook) can subscribe.
  // Added to value below.

  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const openSheet = () => setIsSheetOpen(true)
  const closeSheet = () => setIsSheetOpen(false)
  const toggleSheet = () => setIsSheetOpen(o => !o)

  const value = useMemo<ReceivedOrdersContextValue & { chimeTick: number }>(
    () => ({
      orders,
      count,
      oldestAgeMinutes,
      urgency,
      loading,
      isSheetOpen,
      openSheet,
      closeSheet,
      toggleSheet,
      refetch: () => {
        // Apollo's refetch() on a skipped query executes it anyway — re-guard here
        // so no future caller can re-fire the forbidden query for a role the skip
        // above exists to protect.
        if (canSeeIncomingOrders) refetch()
      },
      chimeTick,
    }),
    [orders, count, oldestAgeMinutes, urgency, loading, isSheetOpen, refetch, chimeTick, canSeeIncomingOrders],
  )

  return (
    <ReceivedOrdersContext.Provider value={value}>{children}</ReceivedOrdersContext.Provider>
  )
}

export const useReceivedOrders = (): ReceivedOrdersContextValue & { chimeTick: number } => {
  const ctx = useContext(ReceivedOrdersContext) as
    | (ReceivedOrdersContextValue & { chimeTick: number })
    | null
  if (!ctx) {
    throw new Error('useReceivedOrders must be used within ReceivedOrdersProvider')
  }
  return ctx
}
